import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Fingerprint, Copy, Download, RefreshCw, ShieldCheck, AlertTriangle,
  Loader2, CheckCircle, ExternalLink, KeyRound, Clock, ArrowRight,
} from 'lucide-react';
import { Button } from '../index';
/* The four SVID buckets, shared — Protect's build surface reads the same two
   fields and must land in the same one. */
import { classifySvidStatus as classifyStatus } from '../../../data/svidStatus';
import { identityAgentPath } from '../../../data/identityRoutes';
import {
  getSpiffeCapability, getAgentSpiffe, refreshAgentSpiffe,
  downloadAgentSpiffeYaml, mintAgentSvid,
} from '../../../services/api';

/* Step 2 (Platform-Eng workflow) — relative-time formatter used by
 * the SVID expiry row. Keeps the dependency surface tiny (no
 * date-fns), and reads correctly on both future ("in 47m") and
 * past ("expired 2h ago") instants. */
function formatRelative(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const deltaMs = target - Date.now();
  const abs = Math.abs(deltaMs);
  const m = Math.round(abs / 60_000);
  const h = Math.round(abs / 3_600_000);
  const d = Math.round(abs / 86_400_000);
  let label;
  if (m < 60)      label = `${m}m`;
  else if (h < 48) label = `${h}h`;
  else             label = `${d}d`;
  return deltaMs >= 0 ? `in ${label}` : `${label} ago`;
}

/* The four buckets live in data/svidStatus.js — Protect's build surface reads
 * the same two fields and must land in the same bucket. This panel used to
 * classify privately and keep its thresholds aligned with the full identity
 * page by hand; a third reader showed that alignment by hand is not alignment.
 * Styling stays here: the words are shared, the design system is not. */
const STATUS_PILL = {
  active:          { label: 'active',          bg: 'bg-v2-success-soft',  text: 'text-v2-success', border: 'border-v2-success/30' },
  expiring:        { label: 'expiring soon',   bg: 'bg-v2-warning-soft',  text: 'text-v2-warning', border: 'border-v2-warning/30' },
  expired:         { label: 'expired',         bg: 'bg-v2-danger-soft',   text: 'text-v2-danger',  border: 'border-v2-danger/30' },
  not_provisioned: { label: 'not provisioned', bg: 'bg-v2-muted', text: 'text-v2-ink-muted',      border: 'border-v2-border-subtle' },
};

/**
 * P0-3 Sprint 1 — SpiffeIdentityPanel
 *
 * <p>Read-mostly panel for the Agent Detail page that surfaces the
 * agent's canonical SPIFFE ID, trust domain, AI System SVID extension
 * claims, SVID metadata (NULL until Sprint 2), and a one-click
 * SPIRE registration entry download.</p>
 *
 * <h3>Three render modes</h3>
 * <ol>
 *   <li><b>Probe pending</b> — small loader.</li>
 *   <li><b>Trust domain not configured</b> — informational banner
 *       explaining the Sprint 1/2/3 rollout and how to enable.</li>
 *   <li><b>Configured</b> — full panel with copy / download / refresh
 *       actions.</li>
 * </ol>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>agentId</code> — required UUID.</li>
 * </ul>
 */
export function SpiffeIdentityPanel({ agentId }) {
  const [capability, setCapability] = useState(null);
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState('loading');  // loading | ready | error | refreshing
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [downloadState, setDownloadState] = useState('idle');
  /* Step 2 — mint button has its own status so the user can see
   * a transient spinner / "Minted" without clobbering the panel-
   * level `state`. Errors from the mint path surface in `mintError`
   * (e.g. 501 NoOp Workload API, 503 SPIRE unavailable) without
   * tearing the panel down to the global error variant. */
  const [mintState, setMintState] = useState('idle'); // idle | minting | done | error
  const [mintError, setMintError] = useState(null);

  const load = useCallback(async () => {
    if (!agentId) return;
    setState('loading');
    setError(null);
    const cap = await getSpiffeCapability();
    if (cap?.ok === false) {
      setState('error');
      setError(cap.error || 'capability probe failed');
      return;
    }
    /* DEF-EXEC-006 fix — apiFetch wraps successful responses in
     * `{ok:true, data:{...}}`. Unwrap to the inner payload so
     * downstream `capability.preview`, `capability.wired`, etc. read
     * correctly. Otherwise the panel falls into a permanent
     * "SPIFFE preview" state even when the backend reports wired=true. */
    setCapability(cap?.data ?? cap);
    const det = await getAgentSpiffe(agentId);
    if (det?.ok === false) {
      setState('error');
      setError(det.error || 'agent SPIFFE lookup failed');
      return;
    }
    /* DEF-EXTELET-V3-SPIFFE-UNWRAP-001 — same apiFetch envelope unwrap
     * as the capability fetch above. Without this, detail.spiffeId /
     * detail.trustDomain read undefined (they live on detail.data), so
     * the panel rendered "—" for every freshly-promoted ExteLet even
     * though G2 had successfully stamped the identity on the agent row. */
    setDetail(det?.data ?? det);
    setState('ready');
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const onRefresh = useCallback(async () => {
    setState('refreshing');
    const r = await refreshAgentSpiffe(agentId);
    if (r?.ok === false) {
      setState('error');
      setError(r.error || 'refresh failed');
      return;
    }
    // Re-fetch the full detail so we get the YAML too.
    const det = await getAgentSpiffe(agentId);
    if (det?.ok !== false) setDetail(det?.data ?? det);
    setState('ready');
  }, [agentId]);

  const onDownload = useCallback(async () => {
    setDownloadState('downloading');
    const r = await downloadAgentSpiffeYaml(agentId);
    if (r?.ok === false) {
      setDownloadState('error');
      return;
    }
    setDownloadState('done');
    setTimeout(() => setDownloadState('idle'), 4000);
  }, [agentId]);

  const onCopy = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch (_) { /* clipboard unavailable */ }
  }, []);

  /* Step 2 (Platform-Eng workflow) — Rotate now. Calls the
   * P0-3 Sprint 2 mint endpoint and patches the detail row in
   * place when the response carries new SVID coordinates. The
   * `agent.write` RBAC check is enforced server-side; the
   * frontend just relays the apiFetch envelope. */
  const onMintSvid = useCallback(async () => {
    if (!agentId) return;
    setMintState('minting');
    setMintError(null);
    const r = await mintAgentSvid(agentId);
    if (r?.ok === false) {
      setMintState('error');
      setMintError(r.error || 'SVID mint failed');
      return;
    }
    // Backend returns { agentId, svidIssuedAt, svidExpiresAt, svidSerial }.
    // Patch the detail row so the user sees the new serial / expiry
    // without waiting for a full re-fetch round-trip.
    setDetail((prev) => prev ? ({
      ...prev,
      svidActive: true,
      svidIssuedAt: r.svidIssuedAt ?? prev.svidIssuedAt,
      svidExpiresAt: r.svidExpiresAt ?? prev.svidExpiresAt,
      svidSerial: r.svidSerial ?? prev.svidSerial,
    }) : prev);
    setMintState('done');
    setTimeout(() => setMintState('idle'), 4000);
  }, [agentId]);

  /* Memoize ABOVE the early returns to keep hook order stable
   * across the loading/error/ready render variants (Rules of
   * Hooks). The derived values are cheap and harmless when
   * `detail` is null. */
  const status = useMemo(
    () => classifyStatus({ svidActive: detail?.svidActive, svidExpiresAt: detail?.svidExpiresAt }),
    [detail?.svidActive, detail?.svidExpiresAt]
  );
  const expiresRelative = useMemo(
    () => formatRelative(detail?.svidExpiresAt),
    [detail?.svidExpiresAt]
  );

  // ── Render variants ─────────────────────────────────────────

  if (state === 'loading') {
    return (
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4 text-[12px] text-v2-ink-muted flex items-center gap-2">
        <Loader2 size={13} className="animate-spin" /> Resolving SPIFFE identity…
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
        <div className="flex items-center gap-2 mb-2">
          <Fingerprint size={14} className="text-v2-ink-muted" />
          <h3 className="text-[13px] font-semibold text-v2-ink m-0">SPIFFE identity</h3>
        </div>
        <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[11.5px] text-v2-warning flex items-center gap-2">
          <AlertTriangle size={13} /> {error}
        </div>
      </div>
    );
  }

  const ext = detail?.aiSystemMetadata || {};
  const isConfigured = !!capability?.configured;
  const workloadApiConnected = !!capability?.workloadApiConnected;
  const pill = STATUS_PILL[status];

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <Fingerprint size={14} className={isConfigured
          ? 'text-v2-action-strong' : 'text-v2-ink-muted'} />
        <h3 className="text-[13px] font-semibold text-v2-ink m-0">SPIFFE identity</h3>
        {isConfigured ? (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-v2-pill text-[10.5px] font-semibold border bg-v2-success-soft text-v2-success border-v2-success/30">
            <ShieldCheck size={10} /> trust domain configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-v2-pill text-[10.5px] font-semibold border bg-v2-warning-soft text-v2-warning border-v2-warning/30">
            preview · default trust domain
          </span>
        )}
        <Button
          variant="ghost" size="sm"
          iconLeft={state === 'refreshing'
            ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />}
          onClick={onRefresh}
          disabled={state === 'refreshing'}
          className="ml-auto"
        >
          Re-derive
        </Button>
      </div>

      {!isConfigured && (
        <div className="mb-3 px-3 py-2 rounded-v2-sm bg-v2-action-soft/40 border border-v2-action/30 text-[11.5px] text-v2-ink leading-relaxed">
          The server is using the default trust domain{' '}
          <code className="font-mono-v2 text-[11px]">{capability?.trustDomain}</code>.
          Configure <code className="font-mono-v2 text-[11px]">agentlets.spiffe.trust-domain</code>{' '}
          to your real trust domain to enable SPIFFE-backed federation.
          {/* DEF-FE-COPY-INTERNAL-JARGON-LEAK-001 — the previous copy
              exposed the internal Sprint 1/2/3 rollout vocabulary
              ("Sprint 1 (this release): identity model + claim schema +
              SPIRE registration YAML"). End users don't speak in
              sprints; reframe as today/next/future capability bullets
              so the message is meaningful to a Compliance officer or
              Security engineer reading the panel. */}
          <br /><strong>Available now:</strong> identity model, claim schema, and SPIRE registration manifest.
          {capability?.sprint2 && (
            <><br /><strong>Coming next:</strong> {capability.sprint2}.</>
          )}
          {capability?.sprint3 && (
            <><br /><strong>On the roadmap:</strong> {capability.sprint3}.</>
          )}
        </div>
      )}

      <div className="space-y-2 mb-3">
        <FieldRow label="SPIFFE ID"
                  value={detail?.spiffeId}
                  onCopy={() => onCopy(detail?.spiffeId, 'spiffeId')}
                  copied={copied === 'spiffeId'} mono />
        <FieldRow label="Trust domain"
                  value={detail?.trustDomain} mono />

        {/* Step 2 (Platform-Eng workflow) — SVID lifecycle surface.
            The data has been on the agent row since V184; only the
            render layer was missing. Pill + serial + countdown + the
            Rotate-now button bring the legacy panel to parity with
            the full /identity/agents/:id page. */}
        <div className="flex items-center gap-3">
          <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-32 shrink-0">
            SVID status
          </span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-v2-pill text-[10.5px] font-semibold border ${pill.bg} ${pill.text} ${pill.border}`}>
            <KeyRound size={10} /> {pill.label}
          </span>
          {status !== 'not_provisioned' && (
            <Button
              variant="ghost" size="sm"
              iconLeft={mintState === 'minting'
                ? <Loader2 size={12} className="animate-spin" />
                : mintState === 'done'
                  ? <CheckCircle size={12} />
                  : <RefreshCw size={12} />}
              disabled={mintState === 'minting' || !workloadApiConnected}
              onClick={onMintSvid}
              className="ml-auto"
              title={workloadApiConnected
                ? 'Mint a fresh X.509-SVID via the SPIRE Workload API'
                : 'SPIRE Workload API is not wired (NoOp client). Configure agentlets.spiffe.workload-api.socket-path to enable.'}
            >
              {mintState === 'minting' ? 'Rotating…'
                : mintState === 'done'   ? 'Rotated'
                : 'Rotate now'}
            </Button>
          )}
        </div>

        {detail?.svidActive ? (
          <>
            <FieldRow label="SVID serial"
                      value={detail?.svidSerial}
                      onCopy={detail?.svidSerial
                        ? () => onCopy(detail?.svidSerial, 'svidSerial') : undefined}
                      copied={copied === 'svidSerial'}
                      valueClass="text-v2-ink font-mono-v2" mono />
            <div className="flex items-baseline gap-3">
              <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-32 shrink-0">
                SVID expires
              </span>
              <code className={`flex-1 text-[11.5px] break-all font-mono-v2 ${
                status === 'expired'  ? 'text-v2-danger'
                : status === 'expiring' ? 'text-v2-warning'
                : 'text-v2-success'
              }`}>
                {detail?.svidExpiresAt || '—'}
              </code>
              {expiresRelative && (
                <span className="inline-flex items-center gap-1 text-[10.5px] text-v2-ink-muted">
                  <Clock size={10} /> {expiresRelative}
                </span>
              )}
            </div>
          </>
        ) : (
          <FieldRow label="SVID"
                    value={workloadApiConnected
                      ? 'not yet minted — click Rotate now to provision'
                      : 'not minted (SPIRE Workload API not wired)'}
                    valueClass="text-v2-ink-muted italic" />
        )}

        {mintState === 'error' && mintError && (
          <div className="ml-32 px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[11px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>{mintError}</span>
          </div>
        )}
      </div>

      {Object.keys(ext).length > 0 && (
        <div className="mb-3 border border-v2-border-subtle rounded-v2-sm overflow-hidden">
          <div className="px-3 py-1.5 bg-v2-muted/30 text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">
            AI System SVID extension
          </div>
          <dl className="divide-y divide-v2-border-subtle">
            {[
              ['Kind',              ext.aiSystemKind],
              ['Autonomy',          ext.aiSystemAutonomy],
              ['Risk tier',         ext.aiSystemRiskTier],
              ['Trust score',       ext.aiSystemTrustScore],
              ['Policy count',      ext.aiSystemPolicyCount],
              ['Frameworks',        (ext.aiSystemFrameworks || []).join(', ') || '—'],
              ['Platform version',  ext.aiSystemPlatformVersion],
              ['Issued at',         ext.aiSystemIssuedAt],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-3 px-3 py-1.5 text-[11.5px]">
                <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold w-32 shrink-0">{k}</dt>
                <dd className="text-v2-ink font-mono-v2 truncate">{v != null && v !== '' ? String(v) : '—'}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="secondary" size="sm"
          iconLeft={downloadState === 'downloading'
            ? <Loader2 size={12} className="animate-spin" />
            : downloadState === 'done'
              ? <CheckCircle size={12} />
              : <Download size={12} />}
          disabled={downloadState === 'downloading'}
          onClick={onDownload}
        >
          {downloadState === 'downloading' ? 'Generating…'
           : downloadState === 'done'        ? 'Downloaded'
           :                                   'Download SPIRE entry'}
        </Button>
        {/* Step 2 — deep link to the full /identity/agents/:id surface
            (IdentityAgentDetailPageV2), where the 7-section SVID
            lifecycle + rotation override + federation inheritance live.
            The legacy panel intentionally stays read-mostly; full
            inheritance editing belongs on the Identity page. */}
        {identityAgentPath(agentId) ? (
          <Link
            to={identityAgentPath(agentId)}
            className="inline-flex items-center gap-1 text-[11.5px] text-v2-action hover:underline"
          >
            View full identity <ArrowRight size={11} />
          </Link>
        ) : null}
        <a
          href="https://spiffe.io/docs/latest/spire-server-cli/#spire-server-entry-create"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11.5px] text-v2-action hover:underline ml-auto"
        >
          SPIRE entry create docs <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

function FieldRow({ label, value, onCopy, copied, valueClass = '', mono = false }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-32 shrink-0">
        {label}
      </span>
      <code
        className={`flex-1 text-[11.5px] break-all ${mono ? 'font-mono-v2' : ''} ${valueClass}`}
      >
        {value || '—'}
      </code>
      {onCopy && value && (
        <button
          type="button"
          onClick={onCopy}
          title="Copy to clipboard"
          className="inline-flex items-center gap-1 text-[10.5px] text-v2-ink-muted hover:text-v2-ink v2-focus-ring"
        >
          {copied ? <CheckCircle size={11} className="text-v2-success" /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}

export default SpiffeIdentityPanel;
